import React, { useState } from 'react';
import { Sparkles, X, ChevronUp, Eye } from 'lucide-react';

/**
 * DemoModeBanner - Floating indicator shown during demo mode
 * 
 * Mobile: Slim bar with "Viewing Demo Data" message
 * Desktop: Original card design in bottom-left
 */
const DemoModeBanner = ({ onCreateBand }) => {
  const [isMinimized, setIsMinimized] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  // Minimized state - tiny floating pill (both mobile & desktop)
  if (isMinimized) {
    return (
      <button
        onClick={() => setIsMinimized(false)}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        className={`
          fixed bottom-20 md:bottom-6 left-4 z-50
          flex items-center gap-2 
          px-3 py-2 
          bg-gradient-to-r from-purple-600 to-pink-600 
          text-white text-sm font-medium
          rounded-full shadow-lg
          transition-all duration-300
          ${isHovered ? 'scale-105 shadow-xl shadow-purple-500/30' : ''}
        `}
      >
        <Sparkles size={14} className="animate-pulse" />
        <span className="hidden sm:inline">Demo</span>
        <ChevronUp size={14} />
      </button>
    );
  }

  return (
    <>
      {/* Desktop Banner - Bottom left card (original design) */}
      <div className="hidden md:block fixed bottom-6 left-4 z-50">
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl shadow-purple-500/10 border border-purple-200 dark:border-purple-800 overflow-hidden">
          {/* Gradient top line */}
          <div className="h-1 bg-gradient-to-r from-purple-500 via-pink-500 to-purple-500" />
          
          <div className="p-4">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl flex items-center justify-center shadow-lg shadow-purple-500/20">
                <Sparkles size={20} className="text-white" />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <p className="font-bold text-gray-800 dark:text-gray-100">
                    Demo Mode
                  </p>
                  <span className="px-2 py-0.5 bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400 text-xs font-semibold rounded-full">
                    SAMPLE DATA
                  </span>
                </div>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Explore how Boply works
                </p>
              </div>
              <button
                onClick={() => setIsMinimized(true)}
                className="ml-2 p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                title="Minimize"
              >
                <X size={18} />
              </button>
            </div>
            
            <button
              onClick={onCreateBand}
              className="w-full py-3 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white font-semibold rounded-xl transition-all duration-200 shadow-lg hover:shadow-xl hover:shadow-purple-500/25"
            >
              Create My Band →
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Banner - Slim top bar */}
      <div className="md:hidden fixed top-16 left-0 right-0 z-30">
        <div className="mx-3 mt-2">
          <div className="bg-white dark:bg-gray-800 border border-purple-200 dark:border-purple-800 rounded-xl px-3 py-2.5 shadow-lg">
            <div className="flex items-center justify-between">
              {/* Left side - message */}
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 bg-gradient-to-br from-purple-500 to-pink-500 rounded-lg flex items-center justify-center">
                  <Eye size={14} className="text-white" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-800 dark:text-gray-100">
                    Viewing Demo Data
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    This is sample data to explore
                  </p>
                </div>
              </div>
              
              {/* Right side - actions */}
              <div className="flex items-center gap-1.5">
                <button
                  onClick={onCreateBand}
                  className="px-3 py-1.5 bg-gradient-to-r from-purple-600 to-pink-600 text-white text-xs font-semibold rounded-lg hover:from-purple-700 hover:to-pink-700 transition-colors shadow-md"
                >
                  Create Band
                </button>
                <button
                  onClick={() => setIsMinimized(true)}
                  className="p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                >
                  <X size={16} />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default DemoModeBanner;